export type AccessDataNetwork = { id: string, name: string };
export type AccessDataFunction = { id: string, name: string };

export type AccessData = {
  functions: AccessDataFunction[];
  networks: AccessDataNetwork[];
  name: string;
  brand_icon: string;
  theme: {
    login_image_url: string | null;
  };
};

// --- Trial generator (landing-page flow) -----------------------------------

export enum ETrialResearchStatus {
  IDLE = 'idle',
  RUNNING = 'running',
  DONE = 'done',
  PARTIAL = 'partial',
  FAILED = 'failed',
  STALE = 'stale',
}

export enum ETrialGenerationStatus {
  NOT_STARTED = 'not_started',
  GATE_RUNNING = 'gate_running',
  GATE_DONE = 'gate_done',
  ENRICH_RUNNING = 'enrich_running',
  ENRICH_DONE = 'enrich_done',
  GATE_FAILED = 'gate_failed',
  ENRICH_PARTIAL = 'enrich_partial',
}

export enum ETrialIndustrySets {
  BASE = 'base',
  SUPERMARKET = 'supermarket',
  APPAREL_FASHION = 'apparel_fashion',
  NON_FOOD_RETAIL = 'non_food_retail',
  HOTELS = 'hotels',
  RESTAURANTS = 'restaurants',
}

/** Keep in sync with node-api `trial-generator/types/trial-modules.ts` (14 tiles). */
export const TRIAL_MODULE_KEYS = [
  'news', 'chat', 'directory', 'events', 'kb', 'ai', 'forms', 'tasks',
  'onboarding', 'elearning', 'performance', 'analytics', 'surveys', 'recognition',
] as const;

export type TrialModuleKey = typeof TRIAL_MODULE_KEYS[number];

export type TrialResearchLocation = {
  place_id: string | null;
  name: string;
  city: string | null;
  is_head_office: boolean;
};

export type TrialBrandColors = { main: string; button: string; link: string };

/** GET …/trial-research `progress` — keep in sync with node-api `types/research-progress.ts`. */
export type TrialResearchProgressStep = {
  key: string;
  status: 'pending' | 'running' | 'done' | 'failed';
};

export type TrialResearchProgress = {
  steps: TrialResearchProgressStep[];
  updated_at?: string;
};

export type TrialResearchPreview = {
  name: string;
  logo_url: string | null;
  icon_url?: string | null;
  colors: TrialBrandColors | null;
  /** Extra hex picks when the primary brand colour guess is wrong. */
  color_options?: string[];
  /** Gemini-grounded mission / purpose for the website-scan step. */
  mission_statement?: string | null;
  /** Firecrawl homepage screenshot for the website-scan step. */
  screenshot_url?: string | null;
  /** AI-generated label (max three words); primary source for the editable industry field. */
  industry_label?: string | null;
  /** Internal preset key for demo content; used when `industry_label` is missing. */
  industry_proposed?: ETrialIndustrySets | string | null;
  confidence: number;
  locations: TrialResearchLocation[];
  locations_total_found: number;
  /** Instagram posts first, else Google Place photos, else website photos. */
  images?: string[];
  socials_found: string[];
  sources_failed: string[];
  /** API-inferred default for the setup “App language” field. */
  suggested_language_locale?: 'nl' | 'en' | null;
};

export type TrialResearchStatus = {
  status: ETrialResearchStatus;
  domain: string | null;
  preview: TrialResearchPreview | null;
  /** Phased pipeline status for the scan UI (website → parallel sources → extract/images). */
  progress?: TrialResearchProgress | null;
  /** Overall scan progress 0–100 from GET …/trial-research. */
  percent?: number;
  error: string | null;
  // Only on anonymous research (landing page).
  token?: string;
  expires_at?: string;
};

export type TrialGenerationStep = {
  key: string;
  phase: 'gate' | 'enrich';
  label: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  message: string | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  /** This step only: 100 when done / skipped / failed, else 0. */
  percent?: number;
  /** How many points of the overall 100 this step is worth when finished. */
  weight_percent?: number;
};

export type TrialGenerationStatus = {
  status: ETrialGenerationStatus;
  phase: 'gate' | 'enrich' | null;
  percent: number;
  steps: TrialGenerationStep[];
  error: { step?: string; message?: string } | null;
  research: { status: ETrialResearchStatus; sources_failed: string[] } | null;
};

/** What the visitor confirmed or changed in the preview; sent with create-trial. */
export type TrialLeadOverrides = {
  name: string;
  /** Visitor-editable industry line (max three words); maps to presets on the API when industry_set is omitted. */
  industry: string;
  industry_set?: ETrialIndustrySets;
  language_locale: 'nl' | 'en';
  locations: Array<{ place_id?: string | null; name: string; is_head_office: boolean }>;
  modules_of_interest: TrialModuleKey[];
  theme?: { main_color: string; button_color: string; link_color: string };
  logo_url?: string;
  logo_data?: string;
};
